import {
  // useMutation,
  // UseMutationOptions,
  // UseMutationResult,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import AmplifyApiService from '../../services/AmplifyApiService';
import IAmplifyApiConfig from '../../shared/interfaces/IAmplifyApiConfig';
import { ICompany } from '../../shared/interfaces/ICompany';

const amplifyApiService = new AmplifyApiService();

const BASE_PATH = 'companies';
const BASE_PATH_PRODUCTS = 'products';

// type RequestEnableMfaProps = {
//   isEnableMfa: boolean;
// };

type GetCompaniesProps = {
  license?: string;
};

type GetRequestableCompaniesResponse = {
  name: string;
  id?: number;
  bpn?: string;
  isCx?: boolean;
  isPacific?: boolean;
};

export type CompanyFilterDrodpdownItem = {
  id?: number;
  companyId?: number;
  companyName?: string;
  companyBpn?: string;
};

export const useListAllBpns = (
  options: UseQueryOptions<Partial<ICompany>[], Error> = {},
): UseQueryResult<Partial<ICompany>[]> => {
  const amplifyApiConfig: IAmplifyApiConfig = {
    apiName: 'PacificApi',
    path: `${BASE_PATH}/get-all-bpns`,
    options: {},
  };

  const query = useQuery<Partial<ICompany>[], Error>({
    queryKey: ['get-all-bpns'],
    queryFn: async (): Promise<Partial<ICompany>[]> => {
      const response = await amplifyApiService.get<{ name: string; bpn: string }[]>(
        amplifyApiConfig,
      );

      // Convert the response to ICompany format
      const formattedResponse = response.map((company) => {
        return {
          companyName: company.name,
          bpn: company.bpn,
        };
      });

      return formattedResponse || [];
    },
    ...options,
  });

  return query;
};

// export const useEnableMfaForCompany = (
//   options: UseMutationOptions<void, Error, RequestEnableMfaProps> = {},
// ): UseMutationResult<void, Error, RequestEnableMfaProps> => {
//   const mutation = useMutation<void, Error, RequestEnableMfaProps>({
//     mutationFn: async (enableMfa: RequestEnableMfaProps) => {
//       const amplifyApiConfig: IAmplifyApiConfig = {
//         apiName: 'PacificApi',
//         path: `${BASE_PATH}/mfa`,
//         options: {
//           body: enableMfa,
//         },
//       };

//       await amplifyApiService.post<RequestEnableMfaProps, unknown>(amplifyApiConfig);
//     },
//     ...options,
//   });

//   return mutation;
// };

export const useGetCompanies = (
  filters: GetCompaniesProps = {},
  options: UseQueryOptions<Partial<ICompany>[], Error> = {},
): UseQueryResult<Partial<ICompany>[]> => {
  const query = useQuery<Partial<ICompany>[], Error>({
    queryKey: ['get-companies', filters],
    queryFn: async (): Promise<Partial<ICompany>[]> => {
      const amplifyApiConfig: IAmplifyApiConfig = {
        apiName: 'PacificApi',
        path: `${BASE_PATH} `,
        options: {
          queryParams: filters,
        },
      };

      const response = await amplifyApiService.get<Partial<ICompany>[]>(amplifyApiConfig);

      return response || [];
    },
    ...options,
  });

  return query;
};

export const useGetCompanyDigitalTwins = (
  bpn: string,
  options: UseQueryOptions<string[], Error> = {},
): UseQueryResult<string[], Error> => {
  const query = useQuery<string[], Error>({
    queryKey: ['get-company-dtr', bpn],
    queryFn: async () => {
      const amplifyApiConfig: IAmplifyApiConfig = {
        apiName: 'PacificApi',
        path: `${BASE_PATH}/get-company-dtr/${bpn}`,
      };

      return await amplifyApiService.get<string[]>(amplifyApiConfig);
    },
    ...options,
  });

  return query;
};

// Get requestable companies (pacific and/or cx)
// This is used in the RequestAdditionalComponentDialog
export const useGetRequestableCompanies = (
  options: UseQueryOptions<GetRequestableCompaniesResponse[], Error> = {},
): UseQueryResult<GetRequestableCompaniesResponse[]> => {
  const query = useQuery<GetRequestableCompaniesResponse[], Error>({
    queryKey: ['get-requestable-companies'],
    queryFn: async (): Promise<GetRequestableCompaniesResponse[]> => {
      const amplifyApiConfig: IAmplifyApiConfig = {
        apiName: 'PacificApi',
        path: `${BASE_PATH}/requestable-companies`,
        options: {},
      };

      const response = await amplifyApiService.get<GetRequestableCompaniesResponse[]>(
        amplifyApiConfig,
      );

      return response || [];
    },
    ...options,
  });

  return query;
};

// Get companies for which the user gave permissions to view their products
export const useGetPermittedCompanies = (
  options: UseQueryOptions<CompanyFilterDrodpdownItem[], Error> = {},
): UseQueryResult<CompanyFilterDrodpdownItem[]> => {
  const query = useQuery<CompanyFilterDrodpdownItem[], Error>({
    queryKey: ['get-permitted-companies'],
    queryFn: async (): Promise<CompanyFilterDrodpdownItem[]> => {
      const amplifyApiConfig: IAmplifyApiConfig = {
        apiName: 'PacificApi',
        path: `${BASE_PATH_PRODUCTS}/companies-with-permission`,
        options: {},
      };

      const response = await amplifyApiService.get<CompanyFilterDrodpdownItem[]>(amplifyApiConfig);

      return response || [];
    },
    ...options,
  });

  return query;
};
